/**
 * MoiPresence.js — Stage 2 of Moi-Mode (v6.34.11+).
 *
 * Renders the Moi mascot + auto-playing pre-recorded greeting on the FOUR student
 * surfaces locked in /app/memory/MOI_MODE_PRD_ADDENDUM_v6_34_10.md §5.1:
 *
 *   1. surface="welcome"      — before any question renders
 *   2. surface="transition"   — between Listening → Speaking → Reading → Writing
 *   3. surface="section_done" — after last item of a section, before submit
 *   4. surface="complete"     — after full exam submit
 *
 * SILENT-default: the component renders NOTHING unless `enabled === true` AND
 * `gradeBand` is one of {"K","1","2"}. Both flags come from `session.moi_mode`,
 * baked by the v6.34.11 resolver at session create time.
 *
 * Regression guard (PRD addendum AC4): this file MUST NOT import from
 * QuestionRenderer or any per-item component. Moi never appears during a live item.
 */
import React, { useEffect, useRef, useState } from "react";

const VISUALS = {
  welcome: "/moi_visuals/moi_wave_K2.png",
  transition: "/moi_visuals/moi_thumbs_up_K2.png",
  section_done: "/moi_visuals/moi_celebrate_K2.png",
  complete: "/moi_visuals/moi_celebrate_K2.png",
};

// Audio slot names map to the keys in /app/backend/scripts/generate_moi_audio.py
const audioSlotFor = (surface, modalityHint) => {
  if (surface === "welcome") return "welcome";
  if (surface === "section_done") return "section_done";
  if (surface === "complete") return "complete";
  // transitions: pick the "to_<next>" line where <next> is the modality the student
  // is ABOUT to start. modalityHint is "speaking" | "reading" | "writing".
  if (surface === "transition") {
    if (modalityHint === "speaking") return "to_speaking";
    if (modalityHint === "reading") return "to_reading";
    if (modalityHint === "writing") return "to_writing";
  }
  return null;
};

const headlineFor = (surface, modalityHint) => {
  if (surface === "welcome") return "Hi! I'm Moi.";
  if (surface === "section_done") return "Nice work!";
  if (surface === "complete") return "You did it!";
  if (surface === "transition") {
    const next = (modalityHint || "next").charAt(0).toUpperCase() + (modalityHint || "next").slice(1);
    return `Next up: ${next}`;
  }
  return "Moi";
};

export const MoiPresence = ({
  enabled,
  gradeBand,
  surface,
  modalityHint,
  onAudioEvent,
  onComplete,          // v6.42.8 — fires when audio ends (or times out)
  minVisibleMs = 5000, // stay on-screen at least 5s even if audio is short
  maxVisibleMs = 25000,// hard cap so a stuck audio never freezes the flow
}) => {
  // SILENT-default — every gate must be true.
  const validGrade = ["K", "1", "2"].includes(gradeBand);
  const validSurface = ["welcome", "transition", "section_done", "complete"].includes(surface);
  const shouldRender = Boolean(enabled) && validGrade && validSurface;

  const audioRef = useRef(null);
  const [audioFailed, setAudioFailed] = useState(false);
  const [hasAutoplayed, setHasAutoplayed] = useState(false);

  const slot = shouldRender ? audioSlotFor(surface, modalityHint) : null;
  const src = slot ? `/moi_audio/${gradeBand}/${slot}.mp3` : null;
  const visual = shouldRender ? VISUALS[surface] : null;

  // Try to autoplay once per mount. Browser autoplay policies may block this
  // (need a prior user gesture); the replay button gives the operator a recovery path.
  useEffect(() => {
    if (!shouldRender || !audioRef.current || hasAutoplayed) return;
    const audio = audioRef.current;
    const tryPlay = async () => {
      try {
        await audio.play();
        setHasAutoplayed(true);
        if (typeof onAudioEvent === "function") onAudioEvent({ event: "autoplay_ok", slot, gradeBand });
      } catch (_e) {
        setAudioFailed(true);
        if (typeof onAudioEvent === "function") onAudioEvent({ event: "autoplay_blocked", slot, gradeBand });
      }
    };
    tryPlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender, slot, gradeBand]);

  // v6.42.8 — audio-aware auto-dismiss. Previously the parent used a hard
  // 6-second setTimeout, which chopped K-2 audio that runs 8-12 seconds
  // (kids need more airtime). Now we listen for the <audio>'s `ended`
  // event AND respect minVisibleMs so a short clip still lingers long
  // enough for the child to read the headline. maxVisibleMs is a
  // safety cap for autoplay-blocked / stalled audio.
  useEffect(() => {
    if (!shouldRender || typeof onComplete !== "function") return;
    const mountedAt = Date.now();
    const audio = audioRef.current;

    // Hard cap — always fire onComplete at most maxVisibleMs after mount.
    const hardTimer = setTimeout(() => onComplete("timeout"), maxVisibleMs);

    // Audio-end path — dismiss when audio finishes, but not before minVisibleMs.
    const finish = () => {
      const elapsed = Date.now() - mountedAt;
      const wait = Math.max(0, minVisibleMs - elapsed);
      setTimeout(() => onComplete("audio_end"), wait);
    };
    let endedHandler = null;
    if (audio) {
      endedHandler = () => finish();
      audio.addEventListener("ended", endedHandler);
    }
    // If autoplay is BLOCKED (audioFailed) the ended event never fires —
    // fall back to a minVisibleMs-based dismiss so the flow still moves.
    let blockedTimer = null;
    if (audioFailed) {
      blockedTimer = setTimeout(() => onComplete("autoplay_blocked"),
                                minVisibleMs);
    }
    return () => {
      clearTimeout(hardTimer);
      if (blockedTimer) clearTimeout(blockedTimer);
      if (audio && endedHandler) audio.removeEventListener("ended", endedHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender, slot, audioFailed]);

  if (!shouldRender) return null;

  const handleReplay = async () => {
    if (!audioRef.current) return;
    try {
      audioRef.current.currentTime = 0;
      await audioRef.current.play();
      setAudioFailed(false);
      if (typeof onAudioEvent === "function") onAudioEvent({ event: "replay_clicked", slot, gradeBand });
    } catch (_e) {
      setAudioFailed(true);
    }
  };

  const isCenterStage = surface === "welcome" || surface === "complete";

  return (
    <div
      data-testid="moi-presence"
      data-moi-surface={surface}
      data-moi-grade={gradeBand}
      className={
        isCenterStage
          ? "flex flex-col items-center justify-center text-center gap-4 py-6"
          : "inline-flex items-center gap-3 py-2"
      }
    >
      {visual && (
        <img
          src={visual}
          alt="Moi mascot"
          className={isCenterStage ? "w-44 h-44 object-contain" : "w-16 h-16 object-contain"}
          data-testid="moi-presence-visual"
        />
      )}
      <div className={isCenterStage ? "max-w-md" : "flex-1"}>
        <div
          className={
            isCenterStage
              ? "text-2xl font-bold text-indigo-900 mb-1"
              : "text-sm font-semibold text-indigo-900"
          }
          data-testid="moi-presence-headline"
        >
          {headlineFor(surface, modalityHint)}
        </div>
        <button
          type="button"
          onClick={handleReplay}
          className="text-xs px-3 py-1 rounded-full border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
          data-testid="moi-presence-replay-btn"
        >
          🔊 Replay greeting
        </button>
        {audioFailed && (
          <div
            className="text-xs text-amber-700 mt-1"
            data-testid="moi-presence-audio-blocked"
          >
            Tap Replay to hear Moi.
          </div>
        )}
      </div>
      {src && (
        <audio
          ref={audioRef}
          src={src}
          preload="auto"
          data-testid="moi-presence-audio"
        />
      )}
    </div>
  );
};

export default MoiPresence;
